import { Alert, Box, Button, FormControl, Grid, Typography } from '@mui/material';

import {
	header,
	organization,
	driver,
	buttonName,
	offsegGetType,
	g500LiteGetType,
	inputPeriodErrorAlert,
	coexistenceGetType,
} from '../../../common/const/commonConst';
import { alert, searchButton } from '../../../common/const/componentSize';
import { DialogType } from '../../../common/type/DialogType';
import { SearchAutocomplete } from '../../../common/component/parts/SearchAutocomplete';
import { SearchPeriod } from '../../../common/component/parts/SearchPeriod';
import { useEffectMasterInfo } from '../../../common/component/hooks/useEffectMasterInfo';
import { useStateSelectedOffsegMasterInfo } from '../../../common/component/hooks/useStateSelectedOffsegMasterInfo';
import { useStateSelectedG500LiteMasterInfo } from '../../../common/component/hooks/useStateSelectedG500LiteMasterInfo';
import { useEffectOffsegMasterInfo } from '../../../common/component/hooks/useEffectOffsegMasterInfo';
import { useEffectG500LiteMasterInfo } from '../../../common/component/hooks/useEffectG500LiteMasterInfo';
import { useStateSearchPeriod } from '../../../common/component/hooks/useStateSearchPeriod';

import { useGetEventVideo } from '../hooks/useGetEventVideo';
import { EventVideoInfoType } from '../../feature/type/EventVideoInfoType';
import { search } from '../../const/eventVideoPageSize';
import theme from '../../../theme';

type PropsType = {
	setEventVideoInfoList: React.Dispatch<React.SetStateAction<EventVideoInfoType[]>>;
	setIsAsyncProcess: React.Dispatch<React.SetStateAction<boolean>>;
	setDialog: React.Dispatch<React.SetStateAction<DialogType>>;
};

/**
 * イベント映像一覧検索
 */
export const EventVideoSearch = (props: PropsType) => {
	const { setEventVideoInfoList, setIsAsyncProcess, setDialog } = props;

	const { masterInfo, setMasterInfo } = useEffectMasterInfo(coexistenceGetType.eventVideo, setDialog); // マスタデータの状態管理用カスタムフック
	const { selectedOffsegMasterInfo, setSelectedOffsegMasterInfo, onChangeOffsegOrganization, onChangeOffsegDriver } =
		useStateSelectedOffsegMasterInfo(); // Offsegの選択したマスタデータの状態管理用カスタムフック
	const { selectedG500LiteMasterInfo, setSelectedG500LiteMasterInfo, onChangeG500LiteOrganization, onChangeG500LiteDriver } =
		useStateSelectedG500LiteMasterInfo(); // G500Liteの選択したマスタデータの状態管理用カスタムフック
	const { startDate, endDate, isInputDateError, isAlertOpened, handleChangeStartDate, handleChangeEndDate, onCloseInputDateErrorAlert } =
		useStateSearchPeriod(); // 検索期間の状態管理用カスタムフック

	const useEffectOffsegMasterInfoInput = {
		getType: offsegGetType.driverInfo,
		selectedOffsegMasterInfo,
		setSelectedOffsegMasterInfo,
		masterInfo,
		setMasterInfo,
		setDialog,
	};
	useEffectOffsegMasterInfo(useEffectOffsegMasterInfoInput); // Offsegマスタデータの状態管理用カスタムフック

	const useEffectG500LiteMasterInfoInput = {
		getType: g500LiteGetType.driverInfo,
		selectedG500LiteMasterInfo,
		setSelectedG500LiteMasterInfo,
		masterInfo,
		setMasterInfo,
		setDialog,
	};
	useEffectG500LiteMasterInfo(useEffectG500LiteMasterInfoInput); // G500Liteマスタデータの状態管理用カスタムフック

	const useGetEventVideoInput = {
		setIsAsyncProcess,
		selectedOffsegMasterInfo,
		selectedG500LiteMasterInfo,
		startDate,
		endDate,
		setDialog,
		setEventVideoInfoList,
	};
	const { onClickSearch } = useGetEventVideo(useGetEventVideoInput); // イベント映像一覧取得用カスタムフック

	return (
		<Box sx={{ height: search.height }} display="flex">
			<Grid container direction="column" justifyContent="flex-start" alignItems="center" spacing={2.5}>
				<Grid item>
					<Typography
						role="lable"
						aria-label={header.offseg}
						position="relative"
						sx={{ fontSize: 17, color: theme.palette.primary.dark, width: search.width }}
					>
						{header.offseg}
					</Typography>
				</Grid>
				<Grid item>
					{/* Offseg組織 */}
					<FormControl>
						<SearchAutocomplete
							id="offsegOrganization"
							options={masterInfo.offsegOrganizationInfoList.map((item) => ({
								id: item.organizationId,
								label: item.organizationName,
							}))}
							label={organization.label}
							value={selectedOffsegMasterInfo.selectedOffsegOrganizationInfo}
							onChange={(e, value) => onChangeOffsegOrganization(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					{/* Offsegドライバー */}
					<FormControl>
						<SearchAutocomplete
							id="offsegDriver"
							options={masterInfo.offsegDriverInfoList.map((item) => ({ id: item.driverId, label: item.driverName }))}
							label={driver.label}
							value={selectedOffsegMasterInfo.selectedOffsegDriverInfo}
							onChange={(e, value) => onChangeOffsegDriver(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					<Typography
						role="lable"
						aria-label={header.g500Lite}
						position="relative"
						sx={{ fontSize: 17, color: theme.palette.primary.dark, width: search.width }}
					>
						{header.g500Lite}
					</Typography>
				</Grid>
				<Grid item>
					{/* G500Lite組織 */}
					<FormControl>
						<SearchAutocomplete
							id="g500LiteOrganization"
							options={masterInfo.g500LiteOrganizationInfoList.map((item) => ({
								id: item.organizationId,
								label: item.organizationName,
							}))}
							label={organization.label}
							value={selectedG500LiteMasterInfo.selectedG500LiteOrganizationInfo}
							onChange={(e, value) => onChangeG500LiteOrganization(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					{/* G500Liteドライバー */}
					<FormControl>
						<SearchAutocomplete
							id="g500LiteDriver"
							options={masterInfo.g500LiteDriverInfoList.map((item) => ({ id: item.driverId, label: item.driverName }))}
							label={driver.label}
							value={selectedG500LiteMasterInfo.selectedG500LiteDriverInfo}
							onChange={(e, value) => onChangeG500LiteDriver(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					<Box sx={{ width: search.width, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
						<Typography role="lable" aria-label={header.searchPeriod} sx={{ fontSize: 17, color: theme.palette.primary.dark }}>
							{header.searchPeriod}
						</Typography>
						<Typography role="lable" aria-label={header.maxSearchPeriodMessage} sx={{ fontSize: 14, color: theme.palette.common.black }}>
							{header.maxSearchPeriodMessage}
						</Typography>
					</Box>
				</Grid>
				<Grid item>
					{/* 検索期間 */}
					<SearchPeriod
						startDate={startDate}
						endDate={endDate}
						handleChangeStartDate={handleChangeStartDate}
						handleChangeEndDate={handleChangeEndDate}
					/>
				</Grid>
				<Grid item>
					<Grid container direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
						<Grid item>
							{/* アラート表示 */}
							<Box sx={{ width: alert.width }}>
								{isAlertOpened && (
									<Alert severity="error" onClose={onCloseInputDateErrorAlert}>
										<Typography sx={{ fontSize: 13.5, color: theme.palette.common.black }}>{inputPeriodErrorAlert.message}</Typography>
									</Alert>
								)}
							</Box>
						</Grid>
						<Grid item>
							{/* 検索ボタン */}
							<Box position="relative" right="6px">
								<Button id="searchButton" variant="contained" onClick={onClickSearch} disabled={isInputDateError} sx={{ width: searchButton.width }}>
									<Typography sx={{ fontSize: 15, color: theme.palette.common.white }}>{buttonName.search}</Typography>
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};
